import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import {
    User,
    UserPlus,
    MagnifyingGlass,
    Buildings,
    Phone,
    Envelope
} from '@phosphor-icons/react';

export function CustomerStep({
    customerTab,
    setCustomerTab,
    customerSearch,
    setCustomerSearch,
    filteredCustomers,
    selectedCustomer,
    formData,
    setFormData
}) {
    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-medium">1</div>
                <h3 className="font-medium">Cliente</h3>
            </div>

            <Tabs value={customerTab} onValueChange={setCustomerTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="existing" className="gap-2" data-testid="tab-existing-customer">
                        <User className="w-4 h-4" />
                        Cliente Esistente
                    </TabsTrigger>
                    <TabsTrigger value="new" className="gap-2" data-testid="tab-new-customer">
                        <UserPlus className="w-4 h-4" />
                        Nuovo Cliente
                    </TabsTrigger>
                </TabsList>

                {/* Cliente esistente */}
                <TabsContent value="existing" className="space-y-3 mt-3">
                    <div className="relative">
                        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Cerca cliente per nome, email, codice fiscale..."
                            value={customerSearch}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                            className="pl-9"
                            data-testid="customer-search-input"
                        />
                    </div>

                    {selectedCustomer ? (
                        <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                        {selectedCustomer.tipo === 'azienda' ? (
                                            <Buildings className="w-5 h-5 text-primary" />
                                        ) : (
                                            <User className="w-5 h-5 text-primary" />
                                        )}
                                    </div>
                                    <div>
                                        <p className="font-medium">
                                            {selectedCustomer.ragione_sociale || `${selectedCustomer.nome} ${selectedCustomer.cognome || ''}`}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            {selectedCustomer.email || selectedCustomer.telefono || selectedCustomer.codice_fiscale}
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setFormData({...formData, customer_id: ''})}
                                >
                                    Cambia
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="border rounded-lg max-h-48 overflow-y-auto">
                            {filteredCustomers.length === 0 ? (
                                <div className="p-4 text-center text-muted-foreground">
                                    <p>Nessun cliente trovato</p>
                                    <Button
                                        type="button"
                                        variant="link"
                                        size="sm"
                                        onClick={() => setCustomerTab('new')}
                                    >
                                        Crea nuovo cliente
                                    </Button>
                                </div>
                            ) : (
                                filteredCustomers.slice(0, 10).map(customer => (
                                    <button
                                        key={customer.id}
                                        type="button"
                                        onClick={() => setFormData({...formData, customer_id: customer.id})}
                                        className="w-full p-3 text-left hover:bg-muted/50 border-b last:border-b-0 flex items-center gap-3"
                                        data-testid={`customer-option-${customer.id}`}
                                    >
                                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                                            {customer.tipo === 'azienda' ? (
                                                <Buildings className="w-4 h-4 text-muted-foreground" />
                                            ) : (
                                                <User className="w-4 h-4 text-muted-foreground" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">
                                                {customer.ragione_sociale || `${customer.nome} ${customer.cognome || ''}`}
                                            </p>
                                            <p className="text-xs text-muted-foreground truncate">
                                                {customer.email || customer.telefono || '-'}
                                            </p>
                                        </div>
                                        <Badge variant="outline" className="text-xs">
                                            {customer.tipo === 'azienda' ? 'Azienda' : 'Privato'}
                                        </Badge>
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </TabsContent>

                {/* Nuovo cliente */}
                <TabsContent value="new" className="space-y-4 mt-3">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-700">
                            <strong>Creazione rapida:</strong> Inserisci i dati minimi. 
                            I dettagli completi saranno raccolti nello step "Inserimento Contratto".
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label>Tipo Cliente</Label>
                        <Select
                            value={formData.new_customer.tipo}
                            onValueChange={(v) => setFormData({
                                ...formData,
                                new_customer: {...formData.new_customer, tipo: v}
                            })}
                        >
                            <SelectTrigger data-testid="new-customer-type">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="privato">Privato</SelectItem>
                                <SelectItem value="azienda">Azienda</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {formData.new_customer.tipo === 'privato' ? (
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Nome *</Label>
                                <Input
                                    value={formData.new_customer.nome}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        new_customer: {...formData.new_customer, nome: e.target.value}
                                    })}
                                    placeholder="Mario"
                                    required={customerTab === 'new'}
                                    data-testid="new-customer-nome"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Cognome</Label>
                                <Input
                                    value={formData.new_customer.cognome}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        new_customer: {...formData.new_customer, cognome: e.target.value}
                                    })}
                                    placeholder="Rossi"
                                    data-testid="new-customer-cognome"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <Label>Ragione Sociale *</Label>
                            <Input
                                value={formData.new_customer.ragione_sociale}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    new_customer: {
                                        ...formData.new_customer,
                                        ragione_sociale: e.target.value,
                                        nome: e.target.value
                                    }
                                })}
                                placeholder="Azienda S.r.l."
                                required={customerTab === 'new' && formData.new_customer.tipo === 'azienda'}
                                data-testid="new-customer-ragione-sociale"
                            />
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                Telefono
                            </Label>
                            <Input
                                value={formData.new_customer.telefono}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    new_customer: {...formData.new_customer, telefono: e.target.value}
                                })}
                                placeholder="+39 333 1234567"
                                data-testid="new-customer-telefono"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="flex items-center gap-1">
                                <Envelope className="w-3 h-3" />
                                Email
                            </Label>
                            <Input
                                type="email"
                                value={formData.new_customer.email}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    new_customer: {...formData.new_customer, email: e.target.value}
                                })}
                                placeholder="email@esempio.it"
                                data-testid="new-customer-email"
                            />
                        </div>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
